/**
 * ExtractionValidationView — Parsed Feedback view for `validate_extraction`
 * runs (`outputSchemaType: 'ExtractionValidationV1'`).
 *
 * The judge reads a transcription back against the page and returns a verdict
 * per question plus the questions it says were missed entirely. What a reader
 * wants from that is "what did it flag, and why" — so flagged questions and
 * missed questions lead, and the `ok` ones collapse to a count. A 40-question
 * paper with two problems should not be forty rows to scroll.
 *
 * Renders the WIRE shape (snake_case), which is what this run persisted — see
 * ExtractionValidationResponseSchema in
 * functions/src/operations/questionExtraction/validationTypes.ts. The stored
 * camelCase `ValidationSummary` is a different, later reduction; this view is
 * deliberately not coupled to it.
 *
 * Parsed as leniently as the backend parses it: every field is optional, and a
 * malformed entry degrades to "less detail on that row" rather than an empty
 * view — losing an observation is the cost, and it should not cost the others.
 */
import { LatexText } from '@shared-ui/latex';

type Verdict = 'ok' | 'suspect' | 'wrong';

interface Issue {
    field?: string;
    severity?: string;
    detail?: string;
    suggestion?: string;
}

interface QuestionVerdict {
    index?: number;
    question_number?: string | number;
    verdict?: Verdict;
    confidence?: number;
    issues?: Issue[];
}

interface MissedQuestion {
    question_number?: string | number;
    page_number?: number;
    detail?: string;
}

export interface ExtractionValidationLike {
    questions?: QuestionVerdict[];
    missed_questions?: MissedQuestion[];
    summary?: string;
}

/** True when the payload looks like a judge verdict. Used by the dispatcher's
 *  fallback path, where the schema is known but the data may be anything. */
export function asExtractionValidation(value: unknown): ExtractionValidationLike | null {
    if (typeof value !== 'object' || value === null) return null;
    const obj = value as Record<string, unknown>;
    if (!Array.isArray(obj.questions) && !Array.isArray(obj.missed_questions)) return null;
    return obj as ExtractionValidationLike;
}

/** Label a question by its printed number, falling back to the judge's 1-based
 *  block index. The index is only meaningful within the chunk the judge saw —
 *  hence the different wording, so it can't be mistaken for a paper number. */
function questionLabel(q: QuestionVerdict): string {
    if (q.question_number != null && String(q.question_number).trim() !== '') return `Q${q.question_number}`;
    return q.index != null ? `#${q.index} in block` : 'Unidentified question';
}

function confidenceLabel(confidence?: number): string | null {
    return typeof confidence === 'number' ? `${Math.round(confidence * 100)}% confident` : null;
}

export function ExtractionValidationView({ data }: { data: ExtractionValidationLike }) {
    const questions = Array.isArray(data.questions) ? data.questions : [];
    const missed = Array.isArray(data.missed_questions) ? data.missed_questions : [];

    // Anything that isn't explicitly ok is worth reading. An absent verdict counts
    // as flagged rather than ok: the judge failing to say is not the judge
    // approving, and silently folding it into the ok count would hide it.
    const flagged = questions.filter(q => q.verdict !== 'ok');
    const okCount = questions.length - flagged.length;

    const counts = {
        wrong: questions.filter(q => q.verdict === 'wrong').length,
        suspect: questions.filter(q => q.verdict === 'suspect').length,
    };

    return (
        <div className="feedback-content">
            <div className="ev-counters">
                <span className="ev-counter"><b>{questions.length}</b> validated</span>
                <span className="ev-counter ev-counter-ok"><b>{okCount}</b> ok</span>
                <span className="ev-counter ev-counter-suspect"><b>{counts.suspect}</b> suspect</span>
                <span className="ev-counter ev-counter-wrong"><b>{counts.wrong}</b> wrong</span>
                <span className="ev-counter ev-counter-missed"><b>{missed.length}</b> missed</span>
            </div>

            {data.summary && (
                <div className="feedback-section" style={{ marginBottom: 12 }}>
                    <h4>Judge summary</h4>
                    <p><LatexText text={data.summary} /></p>
                </div>
            )}

            <div className="feedback-section">
                <h4>Flagged questions</h4>
                {flagged.length === 0 ? (
                    <p className="empty">
                        {questions.length === 0
                            ? 'The judge returned no per-question verdicts.'
                            : 'Nothing flagged — every question the judge read matched the page.'}
                    </p>
                ) : (
                    <ul className="ev-list">
                        {flagged.map((q, i) => {
                            const verdict = q.verdict ?? 'unstated';
                            const confidence = confidenceLabel(q.confidence);
                            const issues = Array.isArray(q.issues) ? q.issues : [];
                            return (
                                <li key={`${q.index ?? 'x'}-${i}`} className="ev-item">
                                    <div className="ev-item-head">
                                        <span className="ev-q">{questionLabel(q)}</span>
                                        <span className={`ev-verdict ev-verdict-${verdict}`}>{verdict}</span>
                                        {confidence && <span className="ev-confidence">{confidence}</span>}
                                    </div>
                                    {issues.length === 0 ? (
                                        // Mirrors the backend, which records a placeholder rather than
                                        // showing a flag with no reason attached.
                                        <p className="ev-issue-detail empty">Flagged without a stated reason.</p>
                                    ) : (
                                        issues.map((issue, j) => (
                                            <div key={j} className="ev-issue">
                                                <div className="ev-issue-tags">
                                                    {issue.field && <span className="ev-field">{issue.field.replace(/_/g, ' ')}</span>}
                                                    {issue.severity && (
                                                        <span className={`ev-severity ev-severity-${issue.severity}`}>{issue.severity}</span>
                                                    )}
                                                </div>
                                                {issue.detail && (
                                                    <p className="ev-issue-detail"><LatexText text={issue.detail} /></p>
                                                )}
                                                {issue.suggestion && (
                                                    <p className="ev-issue-suggestion">
                                                        <span className="ev-suggestion-label">Suggested:</span>{' '}
                                                        <LatexText text={issue.suggestion} />
                                                    </p>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {missed.length > 0 && (
                <div className="feedback-section" style={{ marginTop: 12 }}>
                    <h4>Missed by the extraction</h4>
                    <ul className="ev-list">
                        {missed.map((m, i) => (
                            <li key={i} className="ev-item ev-item-missed">
                                <div className="ev-item-head">
                                    <span className="ev-q">
                                        {m.question_number != null && String(m.question_number).trim() !== ''
                                            ? `Q${m.question_number}`
                                            : 'Unnumbered'}
                                    </span>
                                    {m.page_number != null && <span className="ev-page">page {m.page_number}</span>}
                                </div>
                                {m.detail && <p className="ev-issue-detail"><LatexText text={m.detail} /></p>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
